<script setup lang="ts">
import FormattedNumberInput from '@/components/common/formatted-number-input.vue';
import type { BaseColumnData } from '@/utils/tables/column-data';
import { filterTableData } from '@/utils/tables/search-data.ts';
import {
  getSortIndicator,
  sortData,
  toggleSortOrder,
} from '@/utils/tables/sorting-data';
import { utils } from 'cesg-shared';
import { computed, onMounted, type PropType, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  columns: {
    type: Array as PropType<BaseColumnData[]>,
    required: true,
    default: () => [],
  },
  data: {
    type: Array,
    required: true,
    default: () => [],
  },
  sumColumnKeys: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  rowClickable: {
    type: Boolean,
    required: false,
    default: true,
  },
  enableSearch: {
    type: Boolean,
    required: false,
    default: false,
  },
  inputColumns: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  hasEditPermission: {
    type: Boolean,
    required: false,
    default: false,
  },
  dropdownOptions: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const headerInputs = reactive<Record<string, string>>({});
const searchQuery = ref<string>('');
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filteredRows = ref<any[]>([]);
const timeout = ref<number | null>(null);
const sortState = ref<Record<number, 'none' | 'asc' | 'desc'>>({});
const emit = defineEmits(['header-input']);

function onHeaderInput(columnKey: string, value?: string) {
  if (!value) return;
  headerInputs[columnKey] = value;
  props.data.forEach((row) => {
    (row as Record<string, string>)[columnKey] = value;
  });
  emit('header-input', { columnKey, value });
}

onMounted(() => {
  props.columns.forEach((_, index) => {
    sortState.value[index] = 'none';
  });
  filteredRows.value = [...props.data];
});

watch(
  () => props.data,
  (newValue, oldValue) => {
    filteredRows.value = [...newValue];
    if (oldValue) {
      searchTable();
    }
  },
  { immediate: true, deep: true },
);

const columnSums = computed(() => {
  return props.sumColumnKeys.reduce(
    (acc: Record<string, number>, key: string) => {
      acc[key] = filteredRows.value.reduce(
        (sum, row) => sum + (Number(row[key]) ?? 0),
        0,
      );
      return acc;
    },
    {} as Record<string, number>,
  );
});

const searchTable = () => {
  filteredRows.value = filterTableData(
    props.data,
    props.columns,
    searchQuery.value,
  );
};

const debouncedSearchTable = () => {
  if (timeout.value) clearTimeout(timeout.value);

  timeout.value = window.setTimeout(searchTable, 300);
};

watch(searchQuery, debouncedSearchTable);

const toggleSortState = (colIndex: number) => {
  const currentState = sortState.value[colIndex];
  const newState = toggleSortOrder(currentState);

  Object.keys(sortState.value).forEach((key) => {
    if (Number(key) !== colIndex) {
      sortState.value[Number(key)] = 'none';
    }
  });

  sortState.value[colIndex] = newState;

  if (newState === 'none') {
    searchTable();
  } else {
    const colKey = props.columns[colIndex].key;
    filteredRows.value = sortData(filteredRows.value, colKey, newState);
  }
};
</script>

<template>
  <div class="field" v-if="enableSearch">
    <div class="control">
      <input
        class="input search"
        type="search"
        :placeholder="t('search') + '...'"
        v-model="searchQuery"
      />
    </div>
  </div>
  <table
    class="table is-fullwidth"
    :class="{ 'is-row-clickable': props.rowClickable }"
  >
    <thead>
      <tr>
        <th
          v-for="(column, index) in columns"
          :key="index"
          :class="[{ sortable: column.sortable }, column.align]"
          @click.prevent="column.sortable && toggleSortState(index)"
          :style="`width:${column.columnWidth}`"
        >
          <span class="header-text">{{ column.label }}</span>
          <span
            v-if="column.sortable"
            class="sort-indicator"
            :class="{
              'is-none': sortState[index] === 'none',
              'is-asc': sortState[index] === 'asc',
              'is-desc': sortState[index] === 'desc',
            }"
          >
            {{ getSortIndicator(sortState[index]) }}
          </span>
        </th>
      </tr>

      <tr v-if="inputColumns.length > 0 && hasEditPermission">
        <th v-for="column in columns" :key="column.key">
          <template v-if="inputColumns.includes(column.key)">
            <template v-if="dropdownOptions[column.key]">
              <div class="select unit">
                <select
                  :id="`select-${column.key}`"
                  :name="column.key"
                  v-model="headerInputs[column.key]"
                  @change="
                    onHeaderInput(
                      column.key,
                      ($event.target as HTMLInputElement).value,
                    )
                  "
                >
                  <option
                    v-for="option in dropdownOptions[column.key]"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
            </template>
            <template v-else>
              <FormattedNumberInput
                v-if="column.editableInputType === 'number'"
                :model-value="headerInputs[column.key]"
                @update:model-value="
                  (val) => onHeaderInput(column.key, val?.toString())
                "
              />
              <input
                v-else
                v-model="headerInputs[column.key]"
                class="input"
                type="text"
                placeholder="Alla..."
                @input="
                  onHeaderInput(
                    column.key,
                    ($event.target as HTMLInputElement).value,
                  )
                "
              />
            </template>
          </template>
        </th>
      </tr>
    </thead>
    <tbody>
      <slot name="tbody" :data="filteredRows"></slot>
    </tbody>
    <tfoot v-if="sumColumnKeys.length > 0">
      <tr>
        <td
          v-for="(column, index) in columns"
          :key="index"
          :class="[column.align || '']"
        >
          <span v-if="index === 0">{{ t('sum') }}</span>
          <span v-if="sumColumnKeys.includes(column.key)">
            {{ utils.formatNumber(columnSums[column.key]) }}
          </span>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<style lang="scss">
@use '@/assets/style/variables';
@use 'bulma/sass/utilities/initial-variables' as *;

.input.search {
  width: 20vw;
  @media screen and (max-width: $desktop) {
    width: 85vw;
  }
}

.table {
  &.is-row-clickable {
    tbody tr {
      cursor: pointer;
      transition: background-color 0.3s;

      &:hover {
        background-color: var(--hover-table-row-color);
      }
    }
  }
  tfoot {
    tr td {
      font-family: var(--font-primary-semibold);
    }
  }
}
</style>
